import React from "react";
import CodeBlock from "../components/CodeBlock";
import Analytics, {EVENTS} from "../../../lib/Analytics";

const QuickStart = () => (
  <section id="quick-start" className="developer-section">
    <h2>Quick Start Guide</h2>
    <p>
      Get up and running with the Plex API in four steps. All examples assume
      {" "}<code>https://plexsearch.com</code> as the base URL. You will need an API key
      from your Plex account's Profile &rarr; API Key page.
    </p>
    <p>
      Download the complete Python client:{" "}
      <a href="/api_client.py" download
         onClick={() => Analytics.event(EVENTS.DEVELOPER_DOWNLOAD, {file: "api_client.py"})}>api_client.py</a>
      {" "}&mdash; covers polling, streaming, and agent/chat requests. It imports{" "}
      <a href="/sseclient.py" download
         onClick={() => Analytics.event(EVENTS.DEVELOPER_DOWNLOAD, {file: "sseclient.py"})}>sseclient.py</a>
      {" "}(SSE client with reconnection support), so download both into the same directory.
    </p>

    <h3>Step 1: Verify Connectivity</h3>
    <p>Check that the server is running and get configuration info:</p>
    <CodeBlock
      title="GET /api/version"
      examples={{
        curl: `curl -s /api/version?verbose=true \\
  -H "x-api-key: $PLEX_TOKEN" | jq .`,
        python: `import requests

resp = requests.get(
    "/api/version",
    params={"verbose": "true"},
    headers={"x-api-key": token}
)
info = resp.json()
print(f"Server v{info['version']} - Ready: {info['ready']}")`,
        javascript: `const resp = await fetch(
  "/api/version?verbose=true",
  { headers: { "x-api-key": \`\${token}\` } }
);
const info = await resp.json();
console.log(\`Server v\${info.version} - Ready: \${info.ready}\`);`,
      }}
    />

    <h3>Step 2: Resolve an Entity Name</h3>
    <p>
      Convert a free-text name like "aspirin" into a Plex entity ID. The suggestions
      endpoint returns matching entities with confidence scores:
    </p>
    <CodeBlock
      title="POST /api/suggestions"
      examples={{
        curl: `curl -s /api/suggestions \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '["aspirin"]' | jq '.[] | {id, title: .entity.title, score: ."match-score"}'`,
        python: `resp = requests.post(
    "/api/suggestions",
    headers={"x-api-key": token, "content-type": "application/json"},
    json=["aspirin"]
)
for hit in resp.json():
    print(f"{hit['id']}  {hit['entity']['title']}  (score: {hit.get('match-score', 'n/a')})")
# Output: unichem:161671  Aspirin  (score: 1.0)`,
        javascript: `const resp = await fetch("/api/suggestions", {
  method: "POST",
  headers: {
    "x-api-key": token,
    "Content-Type": "application/json"
  },
  body: JSON.stringify(["aspirin"])
});
const hits = await resp.json();
hits.forEach(h => console.log(\`\${h.id}  \${h.entity.title}  (score: \${h["match-score"]})\`));`,
      }}
    />

    <h3>Step 3: Run a Search</h3>
    <p>
      Use the resolved entity ID to search the knowledge graph. The API supports two
      invocation styles: <strong>polling</strong> (simpler, works everywhere) and{" "}
      <strong>streaming</strong> (real-time progress via SSE). Both return results
      across all categories ranked by relevance.
    </p>

    <h4>Option A: Polling</h4>
    <p>
      Omit <code>stream</code> (or set it to <code>false</code>). The server
      initially returns <code>201</code>, and subsequently returns
      <code>202</code> while the search runs; re-poll every few seconds
      until you receive <code>200</code> with the first page of
      <code>results</code>.
    </p>
    <CodeBlock
      title="POST /api/search (polling)"
      examples={{
        curl: `# Re-POST until status is 200 (202 means still processing)
curl -s /api/search \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq .
# Response while processing: {"status": 202, "progress": 45, "total": 100}
# Final response: {"status": 200, "results": {...}, "totals": {...}}`,
        python: `import time, requests

HEADERS = {"x-api-key": token, "content-type": "application/json"}
body = {"ids": ["unichem:161671"]}

data = {}
while data.get("status") != 200:
    resp = requests.post("/api/search", json=body, headers=HEADERS)
    if resp.status_code >= 400:
        raise ValueError(f"Search failed {resp.status_code}: {resp.content.decode()}")
    data = resp.json()
    if data.get("status") != 200:
        print(f"Status: {data['status']}...")
        time.sleep(5)

for target in data["results"].get("target", []):
    print(f"  {target['rank']:.2f}  {target['entity']['title']}")`,
        javascript: `const headers = { "x-api-key": token, "Content-Type": "application/json" };
const body = JSON.stringify({ ids: ["unichem:161671"] });

let data = {};
while (data.status !== 200) {
  data = await fetch("/api/search", { method: "POST", headers, body }).then(r => r.json());
  if (data.status !== 200) {
    console.log(\`Status: \${data.status}...\`);
    await new Promise(r => setTimeout(r, 5000));
  }
}

data.results.target?.forEach(t =>
  console.log(\`  \${t.rank.toFixed(2)}  \${t.entity.title}\`)
);`,
      }}
    />

    <h4>Option B: Streaming (SSE)</h4>
    <p>
      On 10.x, POST directly to <code>/api/search/sse</code>; the response <em>is</em> the
      SSE stream (or, on a cache hit, the plain JSON result inline — no stream at all). On
      9.x, there is no <code>/sse</code> suffix: pass <code>stream=true</code> as a query
      parameter instead, and the server responds immediately with a{" "}
      <code>stream_id</code> that you connect to separately at{" "}
      <code>/api/stream/&#123;stream_id&#125;</code>. <code>stream=true</code> still works
      on 10.x too (nothing was removed), but <code>/search/sse</code> is the one-call
      version. See the <a href="#sse-streaming">SSE Streaming</a> section for full event
      details.
    </p>
    <CodeBlock
      title="POST /api/search/sse (10.x, one call)"
      examples={{
        curl: `# The response IS the stream (or a plain JSON result, if answered from cache)
curl -s -N "/api/search/sse" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -H "Accept: text/event-stream" \\
  -d '{"ids": ["unichem:161671"]}'
# Receives: event: search_update / search_results / search_error`,
        python: `import json, requests
from sseclient import SSEClient  # use the bundled sseclient.py

HEADERS = {"x-api-key": token, "content-type": "application/json"}

# One call: the response is the SSE stream (or inline JSON on a cache hit)
sse_resp = requests.post("/api/search/sse", headers={**HEADERS, "Accept": "text/event-stream"},
                         json={"ids": ["unichem:161671"]}, stream=True)
if "application/json" in sse_resp.headers.get("content-type", ""):
    data = sse_resp.json()  # answered inline, no stream to read
else:
    for event in SSEClient(sse_resp, events=["*"]).events():
        if event.event == "search_results":
            data = json.loads(event.data)
            break

for t in data.get("results", {}).get("target", []):
    print(f"  {t['rank']:.2f}  {t['entity']['title']}")`,
      }}
    />
    <CodeBlock
      title="POST /api/search?stream=true (9.x, or 10.x's two-step equivalent)"
      examples={{
        curl: `# 1. Initiate with ?stream=true query param
STREAM_ID=$(curl -s "/api/search?stream=true" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq -r '.stream_id')
# Response: {"stream_id": "STREAM_UUID", "status": 200}

# 2. Connect to the SSE stream
curl -s -N "/api/stream/$STREAM_ID" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Accept: text/event-stream"
# Receives: event: search_update / search_results / search_error`,
        javascript: `// /api/stream/{id} requires auth (x-api-key header), and EventSource can't
// send custom headers — so browser code uses fetch + a ReadableStream reader
// instead on either version. 1. Initiate
const init = await fetch("/api/search?stream=true", {
  method: "POST",
  headers: { "x-api-key": token, "Content-Type": "application/json" },
  body: JSON.stringify({ ids: ["unichem:161671"] })
}).then(r => r.json());

// 2. Connect and read the stream with the header attached
const resp = await fetch(\`/api/stream/\${init.stream_id}\`, {
  headers: { "x-api-key": token, Accept: "text/event-stream" }
});
const reader = resp.body.getReader();
const decoder = new TextDecoder();
let buffer = "";
while (true) {
  const { done, value } = await reader.read();
  if (done) break;
  buffer += decoder.decode(value, { stream: true });
  let idx;
  while ((idx = buffer.indexOf("\\n\\n")) !== -1) {
    const block = buffer.slice(0, idx);
    buffer = buffer.slice(idx + 2);
    let type = "message", dataLines = [];
    for (const line of block.split("\\n")) {
      const [field, ...rest] = line.split(":");
      const value_ = rest.join(":").trimStart();
      if (field === "event") type = value_;
      else if (field === "data") dataLines.push(value_);
    }
    if (!dataLines.length) continue;
    const data = JSON.parse(dataLines.join("\\n"));
    if (type === "search_update") {
      console.log(\`Progress: \${data.progress}/\${data.total} — \${data.info}\`);
    } else if (type === "search_results") {
      data.results.target?.forEach(t =>
        console.log(\`  \${t.rank.toFixed(2)}  \${t.entity.title}\`)
      );
    } else if (type === "search_error") {
      console.error("Search failed:", data.error);
    }
  }
}`,
      }}
    />

    <h3>Step 4: Read the Results</h3>
    <p>The response contains results grouped by category. Each result has:</p>
    <ul>
      <li><code>id</code> &mdash; the result entity's ID</li>
      <li><code>rank</code> &mdash; similarity/relevance score (0.0&ndash;1.0)</li>
      <li><code>neighborhood</code> &mdash; community ID this result belongs to in the graph</li>
      <li><code>neighborhood_size</code> &mdash; number of entities in that community</li>
      <li><code>direct_connections</code> &mdash; count of direct edges to your query entities</li>
      <li><code>entity</code> &mdash; full entity data, including <code>title</code> (not <code>name</code>), category, and category-specific fields</li>
    </ul>
    <p>
      Use <code>page_token</code> from the response to fetch the next page of results.
      Use <code>totals</code> to see how many results exist in each category.
    </p>
    <CodeBlock
      title="Paginate results"
      examples={{
        curl: `# Re-POST with page_token from previous response to get the next page
curl -s "/api/search?page_token=CURSOR" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq .`,
        python: `# Collect all pages
body = {"ids": ["unichem:161671"]}
all_targets = []
page_token = None

while True:
    params = {"page_token": page_token} if page_token else {}
    page = requests.post("/api/search", json=body, headers=HEADERS, params=params).json()
    all_targets.extend(page.get("results", {}).get("target", []))
    page_token = page.get("page_token")
    if not page_token:
        break

print(f"Total targets fetched: {len(all_targets)}")`,
        javascript: `// Collect all pages
const body = JSON.stringify({ ids: ["unichem:161671"] });
const allTargets = [];
let pageToken = null;

do {
  const url = pageToken ? \`/api/search?page_token=\${pageToken}\` : "/api/search";
  const page = await fetch(url, { method: "POST", headers, body }).then(r => r.json());
  allTargets.push(...(page.results?.target || []));
  pageToken = page.page_token ?? null;
} while (pageToken);

console.log(\`Total targets fetched: \${allTargets.length}\`);`,
      }}
    />
  </section>
);

export default QuickStart;
